/**
 * Client-side input classification for real-time hints
 * The server does the real classification - this is just for UX
 */

const LICENSE_PATTERN = /^(CGC|CBC|CCC|CPC|CFC|CAC|EC|RG|RB|RC)\s*\d{4,7}$/i;

const FLORIDA_COUNTIES = [
    'Alachua', 'Baker', 'Bay', 'Bradford', 'Brevard', 'Broward', 'Calhoun',
    'Charlotte', 'Citrus', 'Clay', 'Collier', 'Columbia', 'DeSoto', 'Dixie',
    'Duval', 'Escambia', 'Flagler', 'Franklin', 'Gadsden', 'Gilchrist',
    'Glades', 'Gulf', 'Hamilton', 'Hardee', 'Hendry', 'Hernando', 'Highlands',
    'Hillsborough', 'Holmes', 'Indian River', 'Jackson', 'Jefferson', 'Lafayette',
    'Lake', 'Lee', 'Leon', 'Levy', 'Liberty', 'Madison', 'Manatee', 'Marion',
    'Martin', 'Miami-Dade', 'Monroe', 'Nassau', 'Okaloosa', 'Okeechobee',
    'Orange', 'Osceola', 'Palm Beach', 'Pasco', 'Pinellas', 'Polk', 'Putnam',
    'Santa Rosa', 'Sarasota', 'Seminole', 'St. Johns', 'St. Lucie', 'Sumter',
    'Suwannee', 'Taylor', 'Union', 'Volusia', 'Wakulla', 'Walton', 'Washington'
];

const FLORIDA_CITIES = [
    'Miami', 'Tampa', 'Orlando', 'Jacksonville', 'Fort Lauderdale',
    'St. Petersburg', 'Hialeah', 'Tallahassee', 'Cape Coral', 'Pembroke Pines',
    'Hollywood', 'Gainesville', 'Coral Springs', 'Clearwater', 'Palm Bay',
    'Pompano Beach', 'West Palm Beach', 'Lakeland', 'Davie', 'Boca Raton',
    'Sunrise', 'Deltona', 'Plantation', 'Fort Myers', 'Deerfield Beach',
    'Melbourne', 'Largo', 'Palm Coast', 'Boynton Beach', 'Homestead',
    'Kissimmee', 'Sarasota', 'Pensacola', 'Ocala', 'Naples', 'Daytona Beach'
];

const BUSINESS_INDICATORS = /\b(LLC|INC|CORP|CO\.?|COMPANY|CONSTRUCTION|BUILDERS?|ROOFING|PLUMBING|ELECTRIC|CONTRACTING|SERVICES|GROUP|ENTERPRISES?)\b/i;

export type InputHint = 'License Number' | 'County' | 'City' | 'Business Name' | 'Name' | null;

/**
 * Classify user input to provide real-time hints
 * @param input - User's search input
 * @returns Human-readable classification or null if empty
 */
export function classifyInput(input: string): InputHint {
    const trimmed = input.trim();
    if (!trimmed || trimmed.length < 2) return null;

    // Check for license number pattern
    if (LICENSE_PATTERN.test(trimmed)) {
        return 'License Number';
    }

    // Check for partial license number (user is typing)
    const upper = trimmed.toUpperCase();
    if (/^(CGC|CBC|CCC|CPC|CFC|CAC|EC|RG|RB|RC)\d*$/i.test(upper)) {
        return 'License Number';
    }

    // Check for county match
    const lowerInput = trimmed.toLowerCase();
    if (FLORIDA_COUNTIES.some(c => c.toLowerCase() === lowerInput)) {
        return 'County';
    }

    // Check for city match
    if (FLORIDA_CITIES.some(c => c.toLowerCase() === lowerInput)) {
        return 'City';
    }

    // Check for business indicators
    if (BUSINESS_INDICATORS.test(trimmed)) {
        return 'Business Name';
    }

    // Default to name search
    return 'Name';
}
